import React from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  trackClickOpenMAPage,
  trackAnalysisOpenHub,
  trackAnalysisOpenCreation,
  trackAnalysisClickUploadMoreClips,
} from '../../../../analytics';

import { PageSubHeader } from '../../../../components/layout/content';

import { URL } from '../../../../constants';

import useAuthUserStore from '../../../../hooks/useAuthUserStore';
import useSampleProjectData from '../../../../hooks/useSampleProjectData';

import { Person } from '../../../../types';
import { isEmpty, getUrl, getShareLink } from '../../../../utils';

import FaceCircle from '../../components/faceCircle';

interface Props {
  incompletePeople: Person[];
  generatedPeople: Person[];
  handleClickPerson: (person: Person, label: string) => void;
}

const PeopleOtherBlock: React.FC<Props> = (props) => {
  const { eventId = '' } = useParams();

  const { adminMode } = useAuthUserStore();

  const { incompletePeople, generatedPeople, handleClickPerson } = props;

  // sample project data
  const { isSample } = useSampleProjectData(eventId);

  const handleClickGeneratedPerson = (person: Person) => {
    if (adminMode) handleClickPerson(person, 'Generated (Admin)');
    else {
      const shareLink = getShareLink(eventId, person.face.elementId);

      window.open(shareLink, '_blank');
      trackAnalysisOpenCreation();
    }
  };

  return (
    <React.Fragment>
      {!isEmpty(incompletePeople) && (
        <div className="flex flex-col gap-4">
          <PageSubHeader
            heading="Not enough clips"
            subheading={
              <div>
                The people below don't appear in enough clips to create a
                personalised highlight reel.
                {!isSample && (
                  <>
                    {' '}
                    <Link
                      to={getUrl(URL.VB_CLIPS, eventId)}
                      onClick={trackAnalysisClickUploadMoreClips}
                    >
                      Upload more clips
                    </Link>{' '}
                    or{' '}
                    <Link
                      to={getUrl(URL.PROJECT_MISSING_ASSETS, eventId)}
                      onClick={() => trackClickOpenMAPage('analysis')}
                    >
                      email these people
                    </Link>{' '}
                    to fix this issue.
                  </>
                )}
              </div>
            }
          />

          <div className="flex flex-row flex-wrap gap-6">
            {incompletePeople.map((person) => (
              <FaceCircle
                key={person.face.elementId}
                person={person}
                onClick={() => handleClickPerson(person, 'Incomplete')}
              />
            ))}
          </div>
        </div>
      )}

      {!isEmpty(generatedPeople) && (
        <div className="flex flex-col gap-4">
          <PageSubHeader
            heading="Already generated"
            subheading={
              <div>
                The people below already have their personalised videos
                generated. Their videos will not be re-generated.{' '}
                {!isSample && (
                  <>
                    <Link
                      to={getShareLink(eventId)}
                      onClick={trackAnalysisOpenHub}
                      target="_blank"
                    >
                      View their videos
                    </Link>
                    .
                  </>
                )}
              </div>
            }
          />

          <div className="flex flex-row flex-wrap gap-6">
            {generatedPeople.map((person) => (
              <FaceCircle
                key={person.face.elementId}
                person={person}
                onClick={
                  isSample
                    ? undefined
                    : () => handleClickGeneratedPerson(person)
                }
              />
            ))}
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default PeopleOtherBlock;
